import { Component, OnInit } from '@angular/core';
import {
	AbstractModal,
	BroadcastService,
	ModalButtonModel,
	ModalButtonType
} from '@cv-invision/core';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { CustomField } from '../../../model/client-identifier.model';
import { GenericIdentifierData } from './generic-identifier.model';

@Component({
	selector: 'cv-add-edit-generic-identifier',
	templateUrl: './add-edit-generic-identifier.component.html',
	styleUrls: ['./add-edit-generic-identifier.component.scss'],
	providers: [MessageService]
})
export class AddEditGenericIdentifierComponent<T extends { customFields: CustomField[] }> extends AbstractModal implements OnInit {
	public model: T;
	public saving = false;
	public secondFieldValue: string;
	public integrationName: string;
	public isEditing: boolean;
	public labels: { identifierLabel: string; secondFieldLabel: string };
	public showSecondField: boolean;
	public showSecondFieldOnCreate: boolean;
	public secondFieldName: string;
	public disableSecondFieldInEdit: boolean;

	private saveFn: (integrationName: string, model: T) => any;
	private broadcastEvent: string;
	private getSecondFieldValue?: (model: T) => string;
	private atComponentDestroy = new Subject<void>();

	constructor(
		protected broadcastService: BroadcastService,
		private messageService: MessageService
	) {
		super();
	}

	ngOnInit(): void {
		const data = this.data as GenericIdentifierData<T>;
		this.model = { ...data.model };
		this.integrationName = data.integrationName;
		this.isEditing = data.isEditing;
		this.saveFn = data.saveFn;
		this.broadcastEvent = data.broadcastEvent;
		this.labels = data.labels;
		this.showSecondField = data.showSecondField;
		this.showSecondFieldOnCreate = data.showSecondFieldOnCreate;
		this.secondFieldName = data.secondFieldName;
		this.getSecondFieldValue = data.getSecondFieldValue;
		this.disableSecondFieldInEdit = data.disableSecondFieldInEdit ?? false;
		this.secondFieldValue = this.getSecondFieldValue?.(this.model) ?? '';

    if(this.secondFieldName) {
      this.model[this.secondFieldName] = this.secondFieldValue;
    }

		const cancelButton: ModalButtonModel = {
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this)
		};

		const saveButton: ModalButtonModel = {
			name: 'save',
			text: 'Save',
			class: 'btn-sm cv-btn-blue',
			type: ModalButtonType.ACTION,
			disabled: () => !this.setIsFormValid() || this.saving,
			action: () => this.onSave(),
			loading: () => this.saving,
			loadingText: 'Saving'
		};

		this.buttons.push(cancelButton, saveButton);
	}

	/**
	 * Handles modal dismiss via cancel.
	 */
	public onDismiss(): void {
		super.onCancel();
	}

	/**
	 * Checks whether the form is valid (required fields filled, no duplicates).
	 * @returns True if the form is valid, false otherwise.
	 */
	setIsFormValid(): boolean {
		return (
			this.isNonEmpty(this.model['identifier']) &&
			this.areCustomFieldsValid()
		);
	}

	/**
	 * Utility method to check if a string is non-empty after trimming.
	 * @param value - The string value to check.
	 * @returns True if non-empty, false otherwise.
	 */
	private isNonEmpty(value: string): boolean {
		return value && value.trim().length > 0;
	}

	/**
	 * Validates custom fields ensuring all are filled and no duplicates exist.
	 * @returns True if valid, false otherwise.
	 */
	private areCustomFieldsValid(): boolean {
		const fields = this.model.customFields;
		if (!fields || fields.length === 0) return false;

		const allFieldsFilled = fields.every(f =>
			this.isNonEmpty(f.field) && this.isNonEmpty(f.value)
		);

		const noDuplicates = !this.hasDuplicateFields();

		return allFieldsFilled && noDuplicates;
	}

	/**
	 * Checks whether any custom field names are duplicated (case-insensitive).
	 * @returns True if there is at least one duplicated field name, false otherwise.
	 */
	hasDuplicateFields(): boolean {
		const seen = new Set<string>();
		for (const f of this.model.customFields) {
			if (typeof f.field !== 'string') continue;
				const key = f.field.trim().toLowerCase();
			if (seen.has(key)) {
				return true;
			}
			seen.add(key);
		}
		return false;
	}

	/**
	 * Checks whether a given field value is duplicated among custom fields, excluding the field at the current index.
	 * @param field - The field value to check for duplication.
	 * @param currentIndex - The index of the field being checked.
	 * @returns True if the field is duplicated, false otherwise.
	 */
	isDuplicateField(field: string | undefined, currentIndex: number): boolean {
		if (!field || typeof field !== 'string') return false;

		const normalizedField = field.trim().toLowerCase();

		return this.model.customFields
			.filter((f, i) => i !== currentIndex && typeof f.field === 'string')
			.some(f => f.field!.trim().toLowerCase() === normalizedField);
	}

	/**
	 * Adds a new empty custom field to the model.
	 */
	addCustomField(): void {
		this.model.customFields.push(new CustomField());
	}

	/**
	 * Removes a custom field from the model by index.
	 * @param index - The index of the field to remove.
	 */
	deleteCustomField(index: number): void {
		this.model.customFields.splice(index, 1);
	}

	/**
	 * Handles save operation.
	 * Triggers save function and broadcasts result if successful.
	 */
	onSave(): void {
		this.saving = true;
		this.saveFn(this.integrationName, this.model)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: () => {
					this.broadcastService.broadcast({
						name: this.broadcastEvent,
						event: { isEditing: this.isEditing }
					});
					super.onAcceptSuccess({ success: true });
					this.saving = false;
				},
				error: err => {
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: err.error
					});
					this.saving = false;
				}
			});
	}

	/**
	 * Cleans up observables when the component is destroyed.
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next();
		this.atComponentDestroy.unsubscribe();
	}
}
